import React from 'react';
import type { AnalysisSubject } from 'venn-utils';
import { Icon, StudioButton, Tooltip, TooltipPosition } from 'venn-ui-kit';
import { isNil } from 'lodash';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import type { Subject } from 'venn-state';
import {
  openAllocatorSubject,
  allocatorAnalysisSubject,
  isAnyAllocatorOpen,
  isAllocatorOpenState,
  portfolioSubject,
} from 'venn-state';

interface AllocationPanelLauncherActionProps {
  subject: AnalysisSubject;
}

const AllocationPanelLauncherAction = ({ subject }: AllocationPanelLauncherActionProps) => {
  const setAllocatorIsOpen = useSetRecoilState(isAllocatorOpenState);
  const [subjectInAllocator, onOpenSubjectInAllocator] = useRecoilState(openAllocatorSubject);
  const studioSubjectId = subject.portfolio ? portfolioSubject(subject.portfolio.id) : undefined;
  const setAllocatorAnalysisSubject = useSetRecoilState(allocatorAnalysisSubject(studioSubjectId));
  const disabled = useRecoilValue(isAnyAllocatorOpen);

  if (subject.type !== 'portfolio' || isNil(subject.portfolio)) {
    return null;
  }

  const tooltipMessage = generateTooltipMessage(disabled, subject, subjectInAllocator);

  return (
    <Tooltip content={tooltipMessage} position={TooltipPosition.Left}>
      <StudioButton
        size="tiny"
        disabled={disabled}
        onClick={() => {
          setAllocatorIsOpen(true);
          setAllocatorAnalysisSubject(subject);
          onOpenSubjectInAllocator(studioSubjectId);
        }}
      >
        <Icon type="chart-pie" />
      </StudioButton>
    </Tooltip>
  );
};

const generateTooltipMessage = (isDisabled: boolean, subject: AnalysisSubject, subjectInAllocator?: Subject) => {
  if (!isDisabled) {
    return 'View portfolio allocations';
  }
  const openSubjectId =
    subjectInAllocator?.type === 'fund'
      ? subjectInAllocator.fundId
      : subjectInAllocator?.type === 'portfolio'
        ? subjectInAllocator.portfolioId
        : undefined;
  return subject.id !== openSubjectId ? 'Close currently open allocations before viewing this item' : '';
};

export default AllocationPanelLauncherAction;
